"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateTransferTotal = void 0;
const _ = require("lodash");
const models_1 = require("../../models");
const dayjs = require("dayjs");
const hasFreeTransfer_1 = require("./hasFreeTransfer");
const calculateTransferTotal = (transfer_rate_matrix, transfers, check_in_date, number_of_nights, arrival_to_area_id, hotel_id, course_itinerary, has_first_day_golf, has_last_day_golf, arrival_airport_location, departure_airport_location) => {
    let transfer_total = 0;
    let destinations = [];
    const standard_transfer_rate = transfer_rate_matrix.filter((r) => r.type === "STANDARD_RATE");
    const custom_transfer_rates = transfer_rate_matrix.filter((r) => r.type !== "STANDARD_RATE");
    const golf_destinations = _.cloneDeep(course_itinerary);
    let is_first_day_free_transfer = false;
    if (has_first_day_golf) {
        is_first_day_free_transfer = (0, hasFreeTransfer_1.hasFreeTransfer)(hotel_id, course_itinerary[0]?.config?.course?.id, check_in_date, standard_transfer_rate);
    }
    let is_last_day_free_transfer = false;
    if (has_last_day_golf) {
        is_last_day_free_transfer = (0, hasFreeTransfer_1.hasFreeTransfer)(course_itinerary[course_itinerary.length - 1]?.config?.course?.id, hotel_id, dayjs(check_in_date).add(number_of_nights, "day").format("YYYY-MM-DD"), standard_transfer_rate);
    }
    // at this point we know if we have golf on the first / last day and if on those days the golf is a free transfer to the hotel
    const require_golf_transfers = transfers?.type
        ? [models_1.TransferType.GOLF, models_1.TransferType.GOLF_AIRPORT].includes(transfers?.type)
        : false;
    const require_airport_transfers = transfers?.type
        ? [models_1.TransferType.AIRPORT, models_1.TransferType.GOLF_AIRPORT].includes(transfers?.type)
        : false;
    const require_arrival_custom_transfer = has_first_day_golf &&
        !is_first_day_free_transfer &&
        transfers?.arrival_transfer_type === "CUSTOM" &&
        transfers.arrival_airport_enabled &&
        arrival_airport_location &&
        require_airport_transfers;
    const require_departure_custom_transfer = has_last_day_golf &&
        !is_last_day_free_transfer &&
        transfers?.departure_transfer_type === "CUSTOM" &&
        transfers.departure_airport_enabled &&
        departure_airport_location &&
        require_airport_transfers;
    const require_arrival_standard_transfer = ((has_first_day_golf && is_first_day_free_transfer) ||
        !has_first_day_golf ||
        (has_first_day_golf &&
            transfers?.arrival_transfer_type === "STANDARD")) &&
        transfers?.arrival_airport_enabled &&
        arrival_airport_location &&
        require_airport_transfers;
    const require_departure_standard_transfer = ((has_last_day_golf && is_last_day_free_transfer) ||
        !has_last_day_golf ||
        (has_last_day_golf &&
            transfers?.departure_transfer_type === "STANDARD")) &&
        transfers?.departure_airport_enabled &&
        departure_airport_location &&
        require_airport_transfers;
    // update the golf destinations array to remove the golf courses that have free transfers
    if (require_arrival_custom_transfer) {
        golf_destinations[0] = null;
    }
    if (require_departure_custom_transfer) {
        golf_destinations[golf_destinations.length - 1] = null;
    }
    // now get list of all the required destinations
    destinations = golf_destinations
        .filter((x) => x)
        .map((x) => x?.config?.course?.id)
        .filter((id) => id !== undefined);
    // Check if there is golf on the first day and if a custom arrival transfer is enabled.
    // If so, calculate the transfer rate from the arrival airport to the first golf course.
    if (require_arrival_custom_transfer) {
        golf_destinations[0] = null;
        const arrival_date = check_in_date;
        const arrival_location = arrival_airport_location;
        const arrival_to_location = arrival_to_area_id; //hotel.transfer_area.id
        const arrival_via_location = course_itinerary[0]?.config?.course?.id;
        const rate = custom_transfer_rates.filter((r) => {
            return (r.date === arrival_date &&
                ((r.from === arrival_location && r.to === arrival_to_location) ||
                    (r.from === arrival_to_location && r.to === arrival_location)) &&
                r.via === arrival_via_location);
        });
        // if more than one rate, get the cheapest
        const cheapest_rate = _.minBy(rate, (item) => {
            const total = _.get(item, "rate.vehicle.total");
            return total === undefined ? Infinity : total;
        });
        if (cheapest_rate) {
            transfer_total += cheapest_rate.rate.vehicle.total;
        }
    }
    if (require_departure_custom_transfer) {
        golf_destinations[golf_destinations.length - 1] = null;
        const departure_date = dayjs(check_in_date)
            .add(number_of_nights, "day")
            .format("YYYY-MM-DD");
        const departure_location = arrival_to_area_id;
        const departure_to_location = departure_airport_location;
        const departure_via_location = course_itinerary[course_itinerary.length - 1]?.config?.course?.id;
        const rate = custom_transfer_rates.filter((r) => {
            return (r.date === departure_date &&
                ((r.from === departure_location && r.to === departure_to_location) ||
                    (r.from === departure_to_location && r.to === departure_location)) &&
                r.via === departure_via_location);
        });
        // if more than one rate, get the cheapest
        const cheapest_rate = _.minBy(rate, (item) => {
            const total = _.get(item, "rate.vehicle.total");
            return total === undefined ? Infinity : total;
        });
        if (cheapest_rate) {
            transfer_total += cheapest_rate.rate.vehicle.total;
        }
    }
    // REGULAR GOLF TRANSFERS (including free transfers if applicable)
    if (require_golf_transfers) {
        destinations.map((id) => {
            const date = check_in_date;
            const from_location = id;
            const to_location = arrival_to_area_id;
            const rate = standard_transfer_rate.filter((r) => {
                return (r.date === date &&
                    ((r.from === from_location && r.to === to_location) ||
                        (r.from === to_location && r.to === from_location) ||
                        (r.from === from_location && r.to === hotel_id) ||
                        (r.from === hotel_id && r.to === from_location)));
            });
            // if more than one rate, get the cheapest
            const cheapest_rate = _.minBy(rate, (item) => {
                const total = _.get(item, "rate.vehicle.total");
                return total === undefined ? Infinity : total;
            });
            if (cheapest_rate) {
                transfer_total += cheapest_rate.rate.vehicle.total;
            }
        });
    }
    if (require_arrival_standard_transfer) {
        const arrival_date = check_in_date;
        const arrival_location = arrival_airport_location;
        const arrival_to_location = arrival_to_area_id; //hotel.transfer_area.id
        const rate = standard_transfer_rate.filter((r) => {
            return (r.date === arrival_date &&
                ((r.from === arrival_location && r.to === arrival_to_location) ||
                    (r.from === arrival_to_location && r.to === arrival_location)));
        });
        // if more than one rate, get the cheapest
        const cheapest_rate = _.minBy(rate, (item) => {
            const total = _.get(item, "rate.vehicle.total");
            return total === undefined ? Infinity : total;
        });
        if (cheapest_rate) {
            transfer_total += cheapest_rate.rate.vehicle.total;
        }
    }
    if (require_departure_standard_transfer) {
        const departure_date = dayjs(check_in_date)
            .add(number_of_nights, "day")
            .format("YYYY-MM-DD");
        const departure_location = arrival_to_area_id;
        const departure_to_location = departure_airport_location;
        const rate = standard_transfer_rate.filter((r) => {
            return (r.date === departure_date &&
                ((r.from === departure_location && r.to === departure_to_location) ||
                    (r.from === departure_to_location && r.to === departure_location)));
        });
        // if more than one rate, get the cheapest
        const cheapest_rate = _.minBy(rate, (item) => {
            const total = _.get(item, "rate.vehicle.total");
            return total === undefined ? Infinity : total;
        });
        if (cheapest_rate) {
            transfer_total += cheapest_rate.rate.vehicle.total;
        }
    }
    return transfer_total;
};
exports.calculateTransferTotal = calculateTransferTotal;
